import React, { useRef, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { parseYouTube, youtubeFigureHtml, fileLinkHtml } from '../utils/media';

// Bouton de barre d'outils qui ne vole pas le focus à l'éditeur
// (sinon la sélection est perdue, surtout sur téléphone).
function Btn({ onClick, children, disabled }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange, minHeight = 400 }) {
  const ref = useRef(null);
  const savedRange = useRef(null);
  const { uploadImage, uploadDocument } = useAuth();
  const [notice, setNotice] = useState(null); // { type: 'error' | 'info', text }
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Synchronise le contenu quand `value` change de l'extérieur
  // (ex : article chargé depuis l'API après l'affichage de l'éditeur).
  // On ne touche pas au DOM pendant que l'utilisateur tape,
  // sinon le curseur sauterait au début.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  // Mémorise la position du curseur dans l'éditeur, pour pouvoir y revenir
  // après une action qui fait perdre le focus (choix d'un fichier, saisie d'une URL).
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    sel.removeAllRanges();
    if (savedRange.current) {
      sel.addRange(savedRange.current);
    } else {
      // Pas de position mémorisée : on se place à la fin du contenu
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.addRange(range);
    }
  };

  const exec = (cmd, val = null) => {
    restoreSelection();
    document.execCommand(cmd, false, val);
    saveSelection();
    emit();
  };

  // Les images collées depuis Word pointent vers des fichiers de l'ordinateur
  // (file:///C:/...) : elles s'affichent chez vous mais seraient cassées en ligne.
  // On les retire juste après le collage et on prévient.
  const onPaste = () => {
    setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const broken = [...el.querySelectorAll('img')].filter(img => {
        const src = img.getAttribute('src') || '';
        return src.startsWith('file:') || src === '';
      });
      if (broken.length) {
        broken.forEach(img => img.remove());
        setNotice({
          type: 'info',
          text: `${broken.length} image(s) venant de Word ont été retirées : elles pointaient vers des fichiers de votre ordinateur et auraient été cassées sur le site. Ajoutez-les avec le bouton 🖼 image.`
        });
      }
      emit();
    }, 0);
  };

  const insertImage = async () => {
    saveSelection();
    const file = await new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/gif,image/webp';
      input.onchange = (e) => resolve(e.target.files[0]);
      input.click();
    });
    if (!file) return;
    setNotice(null);
    setUploading(true);
    try {
      const url = await uploadImage(file);
      exec('insertImage', url);
    } catch (e) {
      setNotice({ type: 'error', text: `Image non ajoutée : ${e.message}` });
    } finally {
      setUploading(false);
    }
  };

  // Insère un bloc (vidéo, pièce jointe) APRÈS le paragraphe où se trouve le curseur.
  // On n'utilise pas execCommand('insertHTML') : au milieu d'une ligne de texte,
  // Chrome découpe et duplique les blocs insérés.
  const insertBlock = (html) => {
    restoreSelection();
    const el = ref.current;
    const sel = window.getSelection();
    let node = sel.rangeCount ? sel.getRangeAt(0).endContainer : null;
    let block = null;
    while (node && node !== el) {
      if (node.parentNode === el) { block = node; break; }
      node = node.parentNode;
    }
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const last = tpl.content.lastChild;
    if (block) el.insertBefore(tpl.content, block.nextSibling);
    else el.appendChild(tpl.content);
    // curseur dans le paragraphe vide ajouté après le bloc, pour continuer à écrire
    const range = document.createRange();
    range.setStart(last, 0);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    saveSelection();
    emit();
  };

  // Vidéo YouTube : vignette dans l'éditeur, lecteur intégré sur le site
  const insertYouTube = () => {
    saveSelection();
    const url = prompt('Lien de la vidéo YouTube (youtube.com/watch?v=…, youtu.be/…, shorts…) :');
    if (!url) return;
    const yt = parseYouTube(url);
    if (!yt) {
      setNotice({ type: 'error', text: 'Ce lien n\'est pas reconnu comme une vidéo YouTube. Copiez l\'adresse depuis le bouton « Partager » de la vidéo.' });
      return;
    }
    setNotice(null);
    insertBlock(youtubeFigureHtml(yt));
  };

  // Document PDF / Word : envoyé dans S3, lien inséré dans le texte
  const insertDocument = async () => {
    saveSelection();
    const file = await new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      input.onchange = (e) => resolve(e.target.files[0]);
      input.click();
    });
    if (!file) return;
    setNotice(null);
    setUploadingDoc(true);
    try {
      const doc = await uploadDocument(file);
      insertBlock(fileLinkHtml(doc));
    } catch (e) {
      setNotice({ type: 'error', text: `Document non ajouté : ${e.message}` });
    } finally {
      setUploadingDoc(false);
    }
  };

  const insertLink = () => {
    saveSelection();
    const url = prompt('URL ?');
    if (url) exec('createLink', url);
  };

  return (
    <div>
      <div className="editor-toolbar">
        <Btn onClick={() => exec('bold')}><b>B</b></Btn>
        <Btn onClick={() => exec('italic')}><i>I</i></Btn>
        <Btn onClick={() => exec('underline')}><u>U</u></Btn>
        <Btn onClick={() => exec('formatBlock', '<h2>')}>H2</Btn>
        <Btn onClick={() => exec('formatBlock', '<h3>')}>H3</Btn>
        <Btn onClick={() => exec('formatBlock', '<p>')}>¶</Btn>
        <Btn onClick={() => exec('insertUnorderedList')}>• liste</Btn>
        <Btn onClick={() => exec('insertOrderedList')}>1. liste</Btn>
        <Btn onClick={() => exec('formatBlock', '<blockquote>')}>“ ”</Btn>
        <Btn onClick={() => exec('formatBlock', '<pre>')}>{'</>'}</Btn>
        <Btn onClick={insertLink}>🔗 lien</Btn>
        <Btn onClick={insertImage} disabled={uploading}>{uploading ? 'envoi…' : '🖼 image'}</Btn>
        <Btn onClick={insertYouTube}>▶ YouTube</Btn>
        <Btn onClick={insertDocument} disabled={uploadingDoc}>{uploadingDoc ? 'envoi…' : '📎 PDF/Word'}</Btn>
        <Btn onClick={() => exec('removeFormat')}>✕ format</Btn>
      </div>
      {notice && (
        <div className={`flash ${notice.type === 'error' ? 'error' : ''}`} style={{ marginBottom: 0, borderBottom: 'none' }}>
          {notice.text}
          <button type="button" onClick={() => setNotice(null)}
            style={{ marginLeft: '.8rem', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            fermer
          </button>
        </div>
      )}
      <div
        ref={ref}
        className="rich-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onPaste={onPaste}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onBlur={saveSelection}
        style={{ minHeight }}
      />
    </div>
  );
}
